import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { 
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  Filter
} from 'lucide-react';
import { formatCurrency } from '../utils/chartUtils';
import { actualsAPI, clientsAPI } from '../services/api';
import toast from 'react-hot-toast';
import errorHandler from '../utils/errorHandler';

const ActualsGroupedView = () => {
  const [groupedData, setGroupedData] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    month: '',
    clientId: ''
  });

  useEffect(() => {
    loadFormData();
  }, []);

  useEffect(() => {
    loadGroupedData();
  }, [filters]);

  const loadFormData = async () => {
    try {
      const clientsResponse = await clientsAPI.getAll();
      setClients(clientsResponse.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients data');
    }
  };

  const loadGroupedData = async () => {
    try {
      setLoading(true);
      const response = await actualsAPI.getGrouped(filters);
      setGroupedData(response.data);
    } catch (error) {
      console.error('Error loading grouped data:', error);
      const errorMessage = errorHandler.getErrorMessage(error);
      toast.error('Failed to load grouped data: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (variance < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-green-600';
    if (variance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const formatVariance = (variance) => {
    if (variance === null || variance === undefined) return 'N/A';
    return formatCurrency(Math.abs(variance));
  };

  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return 'N/A';
    return `${percentage > 0 ? '+' : ''}${percentage.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading grouped data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Actuals Grouped View</h1>
          <p className="text-gray-600 mt-1">
            Summary of actuals data grouped by client and month
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <select
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Months</option>
              <option value="2025-01">January 2025</option>
              <option value="2025-02">February 2025</option>
              <option value="2025-03">March 2025</option>
              <option value="2025-04">April 2025</option>
              <option value="2025-05">May 2025</option>
              <option value="2025-06">June 2025</option>
              <option value="2025-07">July 2025</option>
              <option value="2025-08">August 2025</option>
              <option value="2025-09">September 2025</option>
              <option value="2025-10">October 2025</option>
              <option value="2025-11">November 2025</option>
              <option value="2025-12">December 2025</option>
            </select>
            <select
              value={filters.clientId}
              onChange={(e) => handleFilterChange('clientId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Grouped Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Client-Month Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Client</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Month</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Resources</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Planned Days</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Avg SOW Rate</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Actual Days</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Avg Charge Rate</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Actual Revenue</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Forecast Revenue</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Revenue Variance</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Days Variance %</th>
                </tr>
              </thead>
              <tbody>
                                 {groupedData.map((item, index) => (
                   <tr key={`${item.client_id}-${item.month_year}`} className="border-b border-gray-100 hover:bg-gray-50">
                     <td className="py-3 px-4 font-medium text-gray-900">{item.client_name}</td>
                     <td className="py-3 px-4 text-gray-600">{item.month_year}</td>
                     <td className="py-3 px-4 text-gray-600">{item.total_resources}</td>
                     <td className="py-3 px-4 text-right text-gray-600">{(parseFloat(item.total_planned_days) || 0).toFixed(2)}</td>
                     <td className="py-3 px-4 text-right text-gray-600">{formatCurrency(parseFloat(item.avg_sow_day_rate) || 0)}</td>
                     <td className="py-3 px-4 text-right text-gray-600">{(parseFloat(item.total_actual_days) || 0).toFixed(2)}</td>
                     <td className="py-3 px-4 text-right text-gray-600">{formatCurrency(parseFloat(item.avg_charge_rate) || 0)}</td>
                     <td className="py-3 px-4 text-right font-medium text-gray-900">{formatCurrency(parseFloat(item.total_actual_revenue) || 0)}</td>
                     <td className="py-3 px-4 text-right text-gray-600">{formatCurrency(parseFloat(item.total_forecast_revenue) || 0)}</td>
                     <td className={`py-3 px-4 text-right font-medium ${getVarianceColor(parseFloat(item.revenue_variance))}`}>
                       <div className="flex items-center justify-end">
                         {getVarianceIcon(parseFloat(item.revenue_variance))}
                         <span className="ml-1">{formatVariance(parseFloat(item.revenue_variance))}</span>
                       </div>
                     </td>
                     <td className={`py-3 px-4 text-right font-medium ${getVarianceColor(parseFloat(item.days_variance_percentage))}`}>
                       {formatPercentage(parseFloat(item.days_variance_percentage))}
                     </td>
                   </tr>
                 ))}
              </tbody>
            </table>
          </div>
          
          {groupedData.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No grouped data found for the selected filters.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActualsGroupedView;
